import axios from "axios"
import * as Sentry from "@sentry/vue"
import { useAuthStore } from "@core/stores/auth.store"

const apiClient = axios.create({
    timeout: 200000, // Temps limite de requête
})

/**
 * Génère un identifiant 24-hex compatible ObjectId Mongoose ET bson.ObjectId
 * Python. Format : 4 bytes timestamp (seconds since epoch) + 8 bytes random.
 *
 * Pourquoi pas UUID v4 (36 chars avec tirets) : @homeys-io/metrics-node
 * passe le header en `_id` Mongoose tel quel ; un UUID v4 fait casser le
 * cast (CastError BSONError) et 0 doc TrackingService est persisté côté
 * api-metier (régression observée 2026-05-02 sur staging, cf. DEV-49).
 *
 * Pas un vrai ObjectId MongoDB (manque machine_id + counter) mais le
 * cast mongoose et bson le valide comme tel : seul l'invariant `^[0-9a-f]{24}$`
 * est vérifié. Suffisamment unique pour la corrélation cross-system
 * (timestamp + 8 random bytes = ~1e19 collisions/sec à éviter).
 */
function generateTrackingId(): string {
    const timestamp = Math.floor(Date.now() / 1000)
        .toString(16)
        .padStart(8, "0")
    const random =
        typeof crypto !== "undefined" &&
        typeof crypto.getRandomValues === "function"
            ? Array.from(crypto.getRandomValues(new Uint8Array(8)))
                  .map((b) => b.toString(16).padStart(2, "0"))
                  .join("")
            : Math.random().toString(16).slice(2, 18).padStart(16, "0")
    return timestamp + random
}

// Intercepteur pour ajouter le token JWT dans les en-têtes
apiClient.interceptors.request.use(
    function (config) {
        // x-tracking-id : pivot de corrélation côté backend (route_handler.py
        // l'accepte déjà) et côté Sentry (tag + breadcrumb).
        const trackingId = generateTrackingId()
        config.headers["x-tracking-id"] = trackingId
        Sentry.getCurrentScope().setTag("tracking_id", trackingId)
        Sentry.addBreadcrumb({
            category: "fetch",
            level: "info",
            message: `${config.method?.toUpperCase() ?? "GET"} ${config.url ?? ""}`,
            data: { tracking_id: trackingId },
        })

        if (config.url?.includes("/api/metier")) {
            // Ne pas ajouter le header Authorization si le service a indiqué x-no-authorization (ex: login)
            if (!config.headers["x-no-authorization"]) {
                const authStore = useAuthStore()
                authStore.setHeaderAuthorization(config)
            }
        }

        return config
    },
    (error) => {
        // Gestion des erreurs de requête
        return Promise.reject(error)
    }
)

/**
 * En cas de 401 avec code invalid_auth (token invalide/expiré), déconnecte et redirige vers la page login.
 */
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const trackingId = error.config?.headers?.["x-tracking-id"]
        if (trackingId) {
            Sentry.addBreadcrumb({
                category: "fetch.error",
                level: "error",
                message: `${error.config?.method?.toUpperCase() ?? "GET"} ${
                    error.config?.url ?? ""
                } → ${error.response?.status ?? "no response"}`,
                data: {
                    tracking_id: trackingId,
                    status: error.response?.status,
                },
            })
        }

        if (typeof window === "undefined") return Promise.reject(error)
        const status = error.response?.status
        const code =
            error.response?.data?.detail?.code ??
            error.response?.data?.detail?.error_code
        const isInvalidAuth = status === 401 && code === "invalid_auth"
        if (isInvalidAuth) {
            const authStore = useAuthStore()
            authStore.logout()
            const { default: router } = await import(
                "@core/plugins/router.plugin"
            )
            await router.push({ name: "PageLogin" })
        }
        return Promise.reject(error)
    }
)

export default apiClient
